import { JTBDOptions } from "@/libraries/growth-unified-profile-client";

import {
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    UserSegmentationTeamType
} from "../../models/user-segmentation";

import { messages } from "./messages";

export const MAX_SELECTABLE = 3;

export const PRIMARY_BLACK = "#101214";

export interface checkboxValues {
    name: string;
    id: string;
    isChecked: boolean;
    priority: number;
    template?: string;
    persona: string | undefined;
    index?: number;
    value: JTBDOptions;
}

export type DefaultValues = Pick<checkboxValues, "template" | "persona">;

export interface JtbdConfiguration {
    checkboxValues: checkboxValues[];
    defaultValues: DefaultValues;
}

export const JTBD_TEMPLATE_TYPE = Object.freeze({
    campaignManagement:
        "com.atlassian.jira-core-project-templates:jira-work-management-campaign-management",
    contentManagement:
        "com.atlassian.jira-core-project-templates:jira-core-simplified-content-management",
    projectManagement:
        "com.atlassian.jira-core-project-templates:jira-core-simplified-project-management",
    taskTracking:
        "com.atlassian.jira-core-project-templates:jira-work-management-task-tracking-team-managed",
    salesPipeline:
        "com.atlassian.jira-core-project-templates:jira-work-management-sales-pipeline-team-managed",
    processControl:
        "com.atlassian.jira-core-project-templates:jira-work-management-process-control-team-managed",
    policyManagement:
        "com.atlassian.jira-core-project-templates:jira-work-management-policy-management-team-managed",
    bugTracking: "com.pyxis.greenhopper.jira:gh-simplified-basic",
    kanban: "kanban",
    scrum: "scrum"
});

export const JTBD_PERSONA = Object.freeze({
    campaignManagement: "Campaign Management Marketing Lead",
    contentManagement: "Content Management Content Marketer",
    projectManagement: "Project Management Marketing Lead"
});

/**
 * Returns kanban template if gate is enabled, otherwise returns the fallback template.
 */
const getTemplate = (
    isGateEnabled: boolean,
    fallbackTemplate: string
): string => {
    return isGateEnabled ? JTBD_TEMPLATE_TYPE.kanban : fallbackTemplate;
};

export const softwareCheckboxVals: checkboxValues[] = [
    {
        name: messages?.softwareOption1?.defaultMessage,
        id: messages?.softwareOption1?.id,
        isChecked: false,
        priority: 6,
        template: JTBD_TEMPLATE_TYPE.kanban,
        persona: undefined,
        index: 1,
        value: "TRACK_BUGS"
    },
    {
        name: messages?.softwareOption2?.defaultMessage,
        id: messages?.softwareOption2?.id,
        isChecked: false,
        priority: 5,
        template: JTBD_TEMPLATE_TYPE.kanban,
        persona: undefined,
        index: 2,
        value: "USE_KANBAN_BOARD"
    },
    {
        name: messages?.softwareOption3?.defaultMessage,
        id: messages?.softwareOption3?.id,
        isChecked: false,
        priority: 4,
        template: JTBD_TEMPLATE_TYPE.kanban,
        persona: undefined,
        index: 3,
        value: "PRIORITIZE_WORK"
    },
    {
        name: messages?.softwareOption4?.defaultMessage,
        id: messages?.softwareOption4?.id,
        isChecked: false,
        priority: 3,
        template: JTBD_TEMPLATE_TYPE.kanban,
        persona: undefined,
        index: 4,
        value: "MAP_WORK_DEPENDENCIES"
    },
    {
        name: messages?.softwareOption5?.defaultMessage,
        id: messages?.softwareOption5?.id,
        isChecked: false,
        priority: 2,
        template: JTBD_TEMPLATE_TYPE.scrum,
        persona: undefined,
        index: 5,
        value: "RUN_SPRINTS"
    },
    {
        name: messages?.softwareOption6?.defaultMessage,
        id: messages?.softwareOption6?.id,
        isChecked: false,
        priority: 1,
        template: JTBD_TEMPLATE_TYPE.scrum,
        persona: undefined,
        index: 6,
        value: "WORK_IN_SCRUM"
    }
];

const marketingCheckboxVals: checkboxValues[] = [
    {
        name: messages?.marketingOption1?.defaultMessage,
        id: messages?.marketingOption1?.id,
        isChecked: false,
        priority: 1,
        template: JTBD_TEMPLATE_TYPE.campaignManagement,
        persona: JTBD_PERSONA.campaignManagement,
        index: 1,
        value: "LAUNCH_CAMPAIGNS"
    },
    {
        name: messages?.marketingOption2?.defaultMessage,
        id: messages?.marketingOption2?.id,
        isChecked: false,
        priority: 2,
        template: JTBD_TEMPLATE_TYPE.contentManagement,
        persona: JTBD_PERSONA.contentManagement,
        index: 2,
        value: "MARKETING_CONTENT"
    },
    {
        name: messages?.marketingOption3?.defaultMessage,
        id: messages?.marketingOption3?.id,
        isChecked: false,
        priority: 4,
        template: JTBD_TEMPLATE_TYPE.projectManagement,
        persona: JTBD_PERSONA.projectManagement,
        index: 3,
        value: "STRATEGIES_AND_GOALS"
    },
    {
        name: messages?.marketingOption4?.defaultMessage,
        id: messages?.marketingOption4?.id,
        isChecked: false,
        priority: 3,
        template: JTBD_TEMPLATE_TYPE.projectManagement,
        persona: JTBD_PERSONA.projectManagement,
        index: 4,
        value: "BUDGETS"
    },
    {
        name: messages?.marketingOption5?.defaultMessage,
        id: messages?.marketingOption5?.id,
        isChecked: false,
        priority: 5,
        template: JTBD_TEMPLATE_TYPE.projectManagement,
        persona: JTBD_PERSONA.projectManagement,
        index: 5,
        value: "PROJECT_PROGRESS"
    },
    {
        name: messages?.marketingOption6?.defaultMessage,
        id: messages?.marketingOption6?.id,
        isChecked: false,
        priority: 6,
        template: JTBD_TEMPLATE_TYPE.projectManagement,
        persona: JTBD_PERSONA.projectManagement,
        index: 6,
        value: "STAKEHOLDERS"
    }
];

export function getMarketingCheckboxVals(
    isGateEnabled: boolean
): checkboxValues[] {
    return marketingCheckboxVals.map((checkboxValue) => ({
        ...checkboxValue,
        template: checkboxValue.template
            ? getTemplate(isGateEnabled, checkboxValue.template)
            : undefined
    }));
}

const operationsCheckboxVals: checkboxValues[] = [
    {
        name: messages?.operationsOption3?.defaultMessage,
        id: messages?.operationsOption3?.id,
        isChecked: false,
        priority: 1,
        template: JTBD_TEMPLATE_TYPE.processControl,
        persona: undefined,
        index: 3,
        value: "SYSTEM_AND_TOOL_EVALUATIONS"
    },
    {
        name: messages?.operationsOption4?.defaultMessage,
        id: messages?.operationsOption4?.id,
        isChecked: false,
        priority: 2,
        template: JTBD_TEMPLATE_TYPE.processControl,
        persona: undefined,
        index: 4,
        value: "COMPLIANCE_AND_RISK_MANAGEMENT"
    },
    {
        name: messages?.operationsOption5?.defaultMessage,
        id: messages?.operationsOption5?.id,
        isChecked: false,
        priority: 3,
        template: JTBD_TEMPLATE_TYPE.projectManagement,
        persona: undefined,
        index: 5,
        value: "PROJECT_PLANNING_AND_COORDINATION"
    },
    {
        name: messages?.operationsOption2?.defaultMessage,
        id: messages?.operationsOption2?.id,
        isChecked: false,
        priority: 5,
        template: JTBD_TEMPLATE_TYPE.taskTracking,
        persona: undefined,
        index: 2,
        value: "AD_HOC_TASK_AND_INCIDENT_MANAGEMENT"
    },
    {
        name: messages?.operationsOption1?.defaultMessage,
        id: messages?.operationsOption1?.id,
        isChecked: false,
        priority: 6,
        template: JTBD_TEMPLATE_TYPE.salesPipeline,
        persona: undefined,
        index: 1,
        value: "MANAGING_CLIENT_AND_VENDOR_RELATIONSHIPS"
    },
    {
        name: messages?.operationsOption6?.defaultMessage,
        id: messages?.operationsOption6?.id,
        isChecked: false,
        priority: 7,
        template: JTBD_TEMPLATE_TYPE.policyManagement,
        persona: undefined,
        index: 6,
        value: "CENTRALIZED_DOCUMENTATION"
    }
];

export function getOperationsCheckboxVals(
    isGateEnabled: boolean
): checkboxValues[] {
    return operationsCheckboxVals.map((checkboxValue) => ({
        ...checkboxValue,
        template: checkboxValue.template
            ? getTemplate(isGateEnabled, checkboxValue.template)
            : undefined
    }));
}

export function getMarketingDefaultValue(
    isGateEnabled: boolean
): DefaultValues {
    return {
        template: getTemplate(
            isGateEnabled,
            JTBD_TEMPLATE_TYPE.campaignManagement
        ),
        persona: JTBD_PERSONA.campaignManagement
    };
}

export const softwareDefaultValue: DefaultValues = {
    template: JTBD_TEMPLATE_TYPE.kanban,
    persona: undefined
};

export function getOperationsDefaultValue(
    isGateEnabled: boolean
): DefaultValues {
    return {
        template: getTemplate(isGateEnabled, JTBD_TEMPLATE_TYPE.processControl),
        persona: undefined
    };
}

export function getTeamTypeToCheckboxMap(
    isGateEnabled: boolean
): Partial<Record<UserSegmentationTeamType, JtbdConfiguration>> {
    return {
        [USER_SEGMENTATION_TEAM_TYPE_MARKETING]: {
            checkboxValues: getMarketingCheckboxVals(isGateEnabled),
            defaultValues: getMarketingDefaultValue(isGateEnabled)
        },
        [USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: {
            checkboxValues: softwareCheckboxVals,
            defaultValues: softwareDefaultValue
        },
        [USER_SEGMENTATION_TEAM_TYPE_OPERATIONS]: {
            checkboxValues: getOperationsCheckboxVals(isGateEnabled),
            defaultValues: getOperationsDefaultValue(isGateEnabled)
        }
    };
}
